import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Search, Shield, User } from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import SuperAdminShell from './SuperAdminShell';
import SuperAdminUserDetailPanel from './SuperAdminUserDetailPanel';

const LIMIT = 50;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function UserListItem({ row, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      className={`w-full text-left px-3 py-3 transition-colors border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 ${
        selected ? 'bg-blue-50 dark:bg-blue-950/40' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium ${
            selected
              ? 'bg-blue-600 text-white'
              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
          }`}
        >
          {(row.name || row.email || '?').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={`truncate text-sm ${selected ? 'font-semibold text-slate-900 dark:text-white' : 'font-medium text-slate-800 dark:text-slate-200'}`}
            >
              {row.name || row.email}
            </span>
            <span className="shrink-0 text-[11px] text-slate-400">{formatDate(row.created_at)}</span>
          </div>
          <div className="truncate text-xs text-slate-500 dark:text-slate-400 mt-0.5">{row.email}</div>
          {(row.banned || row.platform_role === 'super_admin') && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {row.banned ? (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                  Banned
                </Badge>
              ) : null}
              {row.platform_role === 'super_admin' ? (
                <Badge className="gap-0.5 bg-violet-600 text-[10px] px-1.5 py-0 h-4">
                  <Shield className="w-2.5 h-2.5" />
                  Admin
                </Badge>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function SuperAdminUsersPage() {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setSkip(0);
  }, [debouncedQ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/platform/users`, {
        params: { q: debouncedQ || undefined, skip, limit: LIMIT },
      })
      .then((res) => {
        if (cancelled) return;
        setTotal(res.data.total);
        setItems(res.data.items || []);
        const ids = (res.data.items || []).map((u) => u.id);
        setSelectedId((prev) => (prev && ids.includes(prev) ? prev : null));
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load users');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, skip, listVersion]);

  const page = Math.floor(skip / LIMIT) + 1;
  const lastPage = Math.max(1, Math.ceil(total / LIMIT) || 1);

  const onUserUpdated = (updated) => {
    if (updated?.id) {
      setItems((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    }
    setListVersion((v) => v + 1);
  };

  const onUserDeleted = (userId) => {
    setItems((prev) => prev.filter((u) => u.id !== userId));
    setSelectedId(null);
    setMobileShowDetail(false);
    setTotal((t) => Math.max(0, t - 1));
  };

  const selectUser = (id) => {
    setSelectedId(id);
    setMobileShowDetail(true);
  };

  const listPane = (
    <>
      <div className="shrink-0 p-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9 h-9 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
            placeholder="Search users…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-2 px-1">
          {total.toLocaleString()} accounts
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-white dark:bg-slate-900">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 py-16 px-4 text-center">No users match.</p>
        ) : (
          items.map((row) => (
            <UserListItem
              key={row.id}
              row={row}
              selected={selectedId === row.id}
              onSelect={selectUser}
            />
          ))
        )}
      </div>

      <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 text-xs text-slate-500">
        <span>
          Page {page} of {lastPage}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={skip <= 0 || loading}
            onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={skip + LIMIT >= total || loading}
            onClick={() => setSkip((s) => s + LIMIT)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <SuperAdminShell wide inbox title="Users" testId="super-admin-users">
      <div className="flex h-[calc(100dvh-9.5rem)] min-h-[480px] max-h-[900px] rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
        {/* Left: user list (Gmail inbox column) */}
        <aside
          className={`flex flex-col w-full md:w-[min(380px,38%)] lg:w-[360px] shrink-0 border-r border-slate-200 dark:border-slate-800 min-h-0 ${
            mobileShowDetail ? 'hidden md:flex' : 'flex'
          }`}
        >
          {listPane}
        </aside>

        {/* Right: detail panel */}
        <main
          className={`flex-1 min-w-0 flex flex-col min-h-0 bg-slate-50/30 dark:bg-slate-950 ${
            !mobileShowDetail && !selectedId ? 'hidden md:flex' : 'flex'
          }`}
        >
          {mobileShowDetail && selectedId ? (
            <div className="md:hidden shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-2"
                onClick={() => setMobileShowDetail(false)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Users
              </Button>
            </div>
          ) : null}

          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 text-slate-500">
              <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-5 mb-4">
                <User className="w-10 h-10 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Select a user</p>
              <p className="text-xs mt-1 max-w-xs">
                Choose an account from the list to view their plan, add-ons, and documentation.
              </p>
            </div>
          ) : (
            <SuperAdminUserDetailPanel
              embedded
              userId={selectedId}
              onUpdated={onUserUpdated}
              onDeleted={onUserDeleted}
            />
          )}
        </main>
      </div>
    </SuperAdminShell>
  );
}
